import { useEffect, useState } from "react";
import api, { fmtMXN } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

export default function VolumetryTab({ projectId, canManage }) {
  const [data, setData] = useState(null);
  const [profit, setProfit] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/volumetry`).then(({ data }) => setData(data));
    if (canManage) api.get(`/projects/${projectId}/profitability`).then(({ data }) => setProfit(data)).catch(() => {});
  }, [projectId, canManage]);

  if (!data) return <p className="text-slate-500 text-sm">Cargando…</p>;
  const { rows, totals } = data;

  return (
    <div className="space-y-4" data-testid="volumetry-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Importe contratado (conceptos activos)" value={fmtMXN(totals.amount_contracted)} />
        <Kpi label="Importe ejecutado" value={fmtMXN(totals.amount_executed)} color="var(--cyan)" />
        <Kpi label="Importe en excedentes" value={fmtMXN(totals.amount_excess)} color={totals.amount_excess > 0 ? "var(--red)" : "var(--green)"} />
        <Kpi label="Conceptos con excedente" value={totals.concepts_with_excess} color={totals.concepts_with_excess ? "var(--red)" : "var(--green)"} />
      </div>
      {profit && (
        <div className="panel p-4">
          <span className="label-mono" style={{ color: "var(--cyan)" }}>// RENTABILIDAD DE LA OBRA</span>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-2" data-testid="profitability-row">
            <Mini label="Ingreso estimado aprobado" value={fmtMXN(profit.income_estimated_approved)} />
            <Mini label="Cobrado (subtotal)" value={fmtMXN(profit.income_collected)} color="var(--green)" />
            <Mini label="Costo real (gastos)" value={fmtMXN(profit.real_cost)} color="var(--magenta)" />
            <Mini label="Margen" value={fmtMXN(profit.margin)} color={profit.margin >= 0 ? "var(--green)" : "var(--red)"} />
            <Mini label="Margen %" value={profit.margin_pct != null ? `${profit.margin_pct}%` : "—"} color={profit.margin >= 0 ? "var(--green)" : "var(--red)"} />
          </div>
        </div>
      )}
      {totals.concepts_with_excess > 0 && (
        <div className="panel p-3 flex items-center gap-2" style={{ borderColor: "var(--red)" }}>
          <AlertTriangle className="w-4 h-4" style={{ color: "var(--red)" }} />
          <span className="text-xs text-slate-300">Hay volúmenes ejecutados por encima de lo autorizado. El excedente NO es cobrable hasta registrar el convenio o la autorización contractual.</span>
        </div>
      )}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[1250px]" data-testid="volumetry-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-3 py-3">Clave</th><th className="label-mono px-3 py-3">Concepto</th>
            <th className="label-mono px-3 py-3">Unidad</th><th className="label-mono px-3 py-3 text-right">P.U.</th>
            <th className="label-mono px-3 py-3 text-right">Contratado</th><th className="label-mono px-3 py-3 text-right">Adicional</th>
            <th className="label-mono px-3 py-3 text-right">Autorizado</th><th className="label-mono px-3 py-3 text-right">Ejecutado</th>
            <th className="label-mono px-3 py-3 text-right">Aut. interno</th><th className="label-mono px-3 py-3 text-right">Estimado</th>
            <th className="label-mono px-3 py-3 text-right">Pagado</th><th className="label-mono px-3 py-3 text-right">Excedente</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="text-center py-12 text-slate-500">Sin avances registrados aún. Captura números generadores.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} data-testid={`volumetry-row-${r.budget_item_id || r.code || i}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)", background: r.has_excess ? "rgba(220,38,38,0.05)" : undefined }}>
                <td className="px-3 py-2.5 font-mono text-xs">{r.code || "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-200 max-w-[320px]">{r.description}{r.is_extraordinary && <span className="ml-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(249,115,22,0.18)", color: "#f97316" }}>EXTRA</span>}</td>
                <td className="px-3 py-2.5 text-xs text-slate-400">{r.unit || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMXN(r.unit_price)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{r.qty_contracted}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: r.qty_additional || r.qty_client_auth ? "var(--cyan)" : "inherit" }}>{r.qty_additional}{r.qty_client_auth > 0 && <div className="text-[9px]" style={{ color: "#f97316" }}>+{r.qty_client_auth} oficio</div>}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-bold">{r.qty_authorized_total}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: "var(--cyan)" }}>{r.qty_executed}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{r.qty_internal_auth}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{r.qty_estimated}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: "var(--green)" }}>{r.qty_paid}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs font-bold" style={{ color: r.has_excess ? "var(--red)" : "inherit" }}>{r.qty_excess || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Kpi = ({ label, value, color }) => (
  <div className="panel p-3">
    <div className="label-mono">{label}</div>
    <div className="font-display font-black text-xl tabular-nums" style={{ color: color || "var(--text)" }}>{value}</div>
  </div>
);
const Mini = ({ label, value, color }) => (
  <div>
    <div className="label-mono">{label}</div>
    <div className="font-mono font-bold text-sm tabular-nums" style={{ color: color || "var(--text)" }}>{value}</div>
  </div>
);
